import {useQueryClient} from '@tanstack/react-query';
import {PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useUser} from '../../../../provider/user/user-hook';
import {ReviewRealtimePresenceProvider} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-context';
import {ReviewPresenceUpdatePublicationType} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {
    mapRealtimeChangesToReviewPartial,
    mergeReviewWithPartial,
    patchReviewNotesInCache,
} from '../../../../service/reviews/review-realtime-presence/review-realtime-update-helper';
import {getReviewItemUrl, useReviews} from '../../../../service/reviews/reviews';
import {useReviewsCatalogList} from '../../../../service/reviews/reviews-catalogs';
import {useReviewsInfo} from '../../../../service/reviews/reviews-info';
import {useReviewsScrapyCatalogsInfo} from '../../../../service/reviews/reviews-scrapy-catalogs';
import {useReviewsSuggestions} from '../../../../service/reviews/reviews-suggestions';
import {FetchReviewsResultType, ReviewDataType} from '../../../../service/reviews/reviews-type';
import {MainFilterQueryKeyNameEnum} from '../../../../shared-search-parameters';
import {toTrimmedString} from '../../../../util/string';
import {ProvidersIdsEnum} from '../../../../util/type';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {useNewReviewAiSuggest} from '../review-list-page-content/review-list/review/reply-form/new-neuralink-hook';
import {ReviewsFilterDataAsObjectUrlType} from '../review-list-page-content/reviews-filter/reviews-filter-type';
import {SuggestionMapType} from '../reviews-type';

import {
    getSavedReviewsFilterConfig,
    getSavedReviewsFilterQueryFromState,
    getUserConfigWithSavedReviewsFilter,
    hasReviewsFilterQuery,
    ReviewsSavedFilterConfigType,
    ReviewsSavedFilterQueryType,
    ReviewsSavedFilterUiStateType,
} from './reviews-saved-filter-helper';
import {ReviewsStateContext} from './reviews-state-context';
import {getReviewStateFromUrlQuery} from './reviews-state-helper';
import {ReviewsStateRealtimeBridge} from './reviews-state-realtime-bridge';
import {ReviewsStateContextType, ReviewsStateType, ReviewsStateUrlQueryNameEnum} from './reviews-state-type';

type PropsType = PropsWithChildren<Record<string, unknown>>;

// eslint-disable-next-line max-statements
export function ReviewsStateProvider(props: PropsType): JSX.Element {
    const {children} = props;
    const {user, userConfig, mergeUserConfig, isInGettingUserConfig, isInSavingUserConfig} = useUser();
    const [reviewsAnswer, setReviewsAnswer] = useState<Record<number, string>>({});
    const {
        setQuery: setReviewsStateUrlQuery,
        replaceQuery: replaceReviewsStateUrlQuery,
        queries: reviewsQueries,
    } = useUrl<
        | ReviewsFilterDataAsObjectUrlType
        | Record<MainFilterQueryKeyNameEnum, string>
        | Record<ReviewsStateUrlQueryNameEnum, string>
    >();
    const queryClient = useQueryClient();
    const isSavedFilterHydratedRef = useRef(false);
    const [savedFilterConfig, setSavedFilterConfig] = useState<ReviewsSavedFilterConfigType>(() =>
        getSavedReviewsFilterConfig()
    );
    const reviewsState: ReviewsStateType = useMemo(() => getReviewStateFromUrlQuery(reviewsQueries), [reviewsQueries]);

    useEffect(() => {
        if (userConfig) {
            setSavedFilterConfig(getSavedReviewsFilterConfig(userConfig));
        }
    }, [userConfig]);

    const persistSavedFilterConfig = useCallback(
        (nextSavedFilterConfig: ReviewsSavedFilterConfigType) => {
            setSavedFilterConfig(nextSavedFilterConfig);

            mergeUserConfig(getUserConfigWithSavedReviewsFilter(userConfig, nextSavedFilterConfig));
        },
        [mergeUserConfig, userConfig]
    );

    const saveCurrentFilterState = useCallback(
        (query?: ReviewsSavedFilterQueryType, uiState?: ReviewsSavedFilterUiStateType) => {
            if (!savedFilterConfig.enabled) {
                return;
            }

            persistSavedFilterConfig({
                enabled: true,
                query: query || getSavedReviewsFilterQueryFromState(reviewsState),
                uiState: uiState || savedFilterConfig.uiState,
            });
        },
        [persistSavedFilterConfig, reviewsState, savedFilterConfig.enabled, savedFilterConfig.uiState]
    );

    const enableSavedFilter = useCallback(
        (query?: ReviewsSavedFilterQueryType, uiState?: ReviewsSavedFilterUiStateType) => {
            persistSavedFilterConfig({
                enabled: true,
                query: query || getSavedReviewsFilterQueryFromState(reviewsState),
                uiState: uiState || savedFilterConfig.uiState,
            });
        },
        [persistSavedFilterConfig, reviewsState, savedFilterConfig.uiState]
    );

    const disableSavedFilter = useCallback(() => {
        persistSavedFilterConfig({
            enabled: false,
            query: {},
            uiState: savedFilterConfig.uiState,
        });
    }, [persistSavedFilterConfig, savedFilterConfig.uiState]);

    const resetSavedFilter = useCallback(
        (query?: ReviewsSavedFilterQueryType, uiState?: ReviewsSavedFilterUiStateType) => {
            if (!savedFilterConfig.enabled) {
                return;
            }

            persistSavedFilterConfig({
                enabled: true,
                query: query || {},
                uiState: uiState || savedFilterConfig.uiState,
            });
        },
        [persistSavedFilterConfig, savedFilterConfig.enabled, savedFilterConfig.uiState]
    );

    useEffect(() => {
        if (isSavedFilterHydratedRef.current || isInGettingUserConfig || !savedFilterConfig.enabled) {
            return;
        }

        isSavedFilterHydratedRef.current = true;

        if (hasReviewsFilterQuery(reviewsQueries) || Object.keys(savedFilterConfig.query).length === 0) {
            return;
        }

        replaceReviewsStateUrlQuery(savedFilterConfig.query);
    }, [
        isInGettingUserConfig,
        replaceReviewsStateUrlQuery,
        reviewsQueries,
        savedFilterConfig.enabled,
        savedFilterConfig.query,
    ]);

    const setReviewAnswer = useCallback((reviewId: number, answer: string) => {
        setReviewsAnswer((previousState) => ({...previousState, [reviewId]: answer}));
    }, []);

    const appendReviewAnswerChunk = useCallback((reviewId: number, chunk: string) => {
        setReviewsAnswer((previous) => ({
            ...previous,
            [reviewId]: (previous[reviewId] || '') + chunk,
        }));
    }, []);

    const {requestSuggest, isGeneratingForReview, isBulkGenerate} = useNewReviewAiSuggest((textValue, reviewPk) =>
        appendReviewAnswerChunk(reviewPk, textValue)
    );

    const getReviewAnswer = useCallback(
        (reviewId: number) => {
            return reviewsAnswer[reviewId] || '';
        },
        [reviewsAnswer]
    );

    const {
        data: reviews = null,
        isFetching: isReviewsInProgress,
        refetch: refreshReviews,
        pagination: cursorPagination,
        queryKey,
        reviewIds,
    } = useReviews(reviewsState);

    const [reviewsSearchDraft, setReviewsSearchDraft] = useState(
        () => reviewsState[ReviewsStateUrlQueryNameEnum.search]
    );

    const reviewsSearchApplied = reviewsState[ReviewsStateUrlQueryNameEnum.search];

    useEffect(() => {
        setReviewsSearchDraft(reviewsSearchApplied);
    }, [reviewsSearchApplied]);

    const submitReviewsSearch = useCallback(() => {
        const trimmed = toTrimmedString(reviewsSearchDraft);
        const applied = toTrimmedString(reviewsSearchApplied);

        if (trimmed === applied) {
            return;
        }

        setReviewsStateUrlQuery({[ReviewsStateUrlQueryNameEnum.search]: trimmed});
        cursorPagination.changePagePagination(1);
    }, [cursorPagination, reviewsSearchApplied, reviewsSearchDraft, setReviewsStateUrlQuery]);

    const clearReviewsSearch = useCallback(() => {
        setReviewsSearchDraft('');

        if (toTrimmedString(reviewsSearchApplied) !== '') {
            setReviewsStateUrlQuery({[ReviewsStateUrlQueryNameEnum.search]: ''});
            cursorPagination.changePagePagination(1);
        }
    }, [cursorPagination, reviewsSearchApplied, setReviewsStateUrlQuery]);

    const {
        data: reviewsCatalogList = null,
        isFetching: reviewsCatalogListIsInProgress,
        refetch: refreshCatalogs,
    } = useReviewsCatalogList();

    const googleMapsReviewIds = useMemo(
        () =>
            reviews?.results
                .filter((item) => item.catalogId === ProvidersIdsEnum.googleMapsNew)
                .map((review) => review.id) || [],
        [reviews]
    );

    const patchReviewInCache = useCallback(
        (reviewId: number, partialReviewData: Partial<ReviewDataType>) => {
            queryClient.setQueryData([queryKey], (oldData?: FetchReviewsResultType) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    results: oldData.results.map((review) => {
                        return reviewId === review.id ? mergeReviewWithPartial(review, partialReviewData) : review;
                    }),
                };
            });

            queryClient.setQueryData([getReviewItemUrl(reviewId)], (oldData?: ReviewDataType) => {
                if (!oldData) {
                    return oldData;
                }

                return mergeReviewWithPartial(oldData, partialReviewData);
            });

            if ('notes' in partialReviewData) {
                patchReviewNotesInCache(queryClient, reviewId, reviewIds, partialReviewData.notes ?? []);
            }
        },
        [queryClient, queryKey, reviewIds]
    );

    const findBrandIdForReview = useCallback(
        (reviewId: number): number | undefined => {
            const listData = queryClient.getQueryData<FetchReviewsResultType>([queryKey]);
            const brandIdFromList = listData?.results.find((review) => review.id === reviewId)?.brand?.id;

            if (brandIdFromList) {
                return brandIdFromList;
            }

            const itemData = queryClient.getQueryData<ReviewDataType>([getReviewItemUrl(reviewId)]);

            return itemData?.brand?.id;
        },
        [queryClient, queryKey]
    );

    const applyRemoteReviewUpdate = useCallback(
        (payload: ReviewPresenceUpdatePublicationType) => {
            patchReviewInCache(payload.reviewId, mapRealtimeChangesToReviewPartial(payload.changes));
        },
        [patchReviewInCache]
    );

    const {data: resultSuggestions, refetch: refreshSuggestions} = useReviewsSuggestions(reviewIds);
    const suggestionMap: SuggestionMapType = useMemo(() => {
        return Object.fromEntries(
            (resultSuggestions?.results || []).map((suggestion) => [suggestion.reviewId, suggestion])
        );
    }, [resultSuggestions?.results]);

    const {data: reviewsInfo = null, refetch: refreshReviewsInfo} = useReviewsInfo(reviewIds);
    const {data: reviewsScrapyCatalogsList} = useReviewsScrapyCatalogsInfo();

    const baseContextValue = useMemo(
        () => ({
            reviewsState,
            setReviewsStateUrlQuery,
            refreshReviews,
            reviewsCatalogList,
            reviewsCatalogListIsInProgress,
            reviewsScrapyCatalogsList,
            suggestionMap,
            refreshSuggestions,
            reviewsInfo,
            refreshReviewsInfo,
            refreshCatalogs,
            isReviewsInProgress,
            cursorPagination,
            reviews,
            reviewIds,
            googleMapsReviewIds,
            isShowAutotags: user?.profile?.isShowAutotags,
            isSavedFilterEnabled: savedFilterConfig.enabled,
            isSavedFilterLoading: isInGettingUserConfig,
            isSavedFilterSaving: isInSavingUserConfig,
            savedFilterUiState: savedFilterConfig.uiState,
            enableSavedFilter,
            disableSavedFilter,
            saveCurrentFilterState,
            resetSavedFilter,
            reviewsAnswer,
            setReviewAnswer,
            getReviewAnswer,
            appendReviewAnswerChunk,
            isReplyGenerating: isGeneratingForReview,
            requestAIGenerate: requestSuggest,
            isBulkGenerate,
            reviewsSearchDraft,
            setReviewsSearchDraft,
            submitReviewsSearch,
            clearReviewsSearch,
        }),
        [
            reviewsState,
            setReviewsStateUrlQuery,
            refreshReviews,
            reviewsCatalogList,
            reviewsCatalogListIsInProgress,
            reviewsScrapyCatalogsList,
            suggestionMap,
            refreshSuggestions,
            reviewsInfo,
            refreshReviewsInfo,
            refreshCatalogs,
            isReviewsInProgress,
            cursorPagination,
            reviews,
            reviewIds,
            googleMapsReviewIds,
            user?.profile?.isShowAutotags,
            savedFilterConfig.enabled,
            savedFilterConfig.uiState,
            isInGettingUserConfig,
            isInSavingUserConfig,
            enableSavedFilter,
            disableSavedFilter,
            saveCurrentFilterState,
            resetSavedFilter,
            reviewsAnswer,
            setReviewAnswer,
            getReviewAnswer,
            appendReviewAnswerChunk,
            isGeneratingForReview,
            requestSuggest,
            isBulkGenerate,
            reviewsSearchDraft,
            submitReviewsSearch,
            clearReviewsSearch,
        ]
    );

    return (
        <ReviewRealtimePresenceProvider onReviewUpdate={applyRemoteReviewUpdate}>
            <ReviewsStateRealtimeBridge
                baseContextValue={baseContextValue}
                findBrandIdForReview={findBrandIdForReview}
                patchReviewInCache={patchReviewInCache}
                queryClient={queryClient}
                queryKey={queryKey}
            >
                {children}
            </ReviewsStateRealtimeBridge>
        </ReviewRealtimePresenceProvider>
    );
}

export function useReviewStateHook(): ReviewsStateContextType {
    const context: ReviewsStateContextType | null = useContext(ReviewsStateContext);

    if (!context) {
        throw new Error('useReviewStateHook must be used within a ReviewsStateProvider');
    }

    return context;
}
